import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Cookie, X } from 'lucide-react';
import { usePlatformSettings } from '@/lib/platformSettings';
import { getConsent, setConsent } from '@/lib/analytics';

/**
 * ConsentBanner — DPDP / GDPR-friendly opt-in banner shown to first-time
 * visitors whenever `platform_settings.analytics.consent_required` is on.
 *
 * Behaviours:
 *   - Reads/writes the consent choice via localStorage (persists across
 *     visits and tabs).
 *   - Emits `indusfact:consent-change` so AnalyticsProvider can boot
 *     tracking immediately after Accept.
 *   - Never shown to staff (admin/moderator) — internal sessions bypass
 *     tracking entirely so the banner is unnecessary noise for them.
 *   - Hidden on `/admin` and `/moderator` routes as a belt-and-braces
 *     guard against staff seeing it if they're logged out.
 */
export const ConsentBanner = () => {
  const { analytics } = usePlatformSettings();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Decide visibility once platform settings arrive.
    if (!analytics) return;
    const consentRequired = analytics.consent_required !== false;
    const hasChoice = !!getConsent();
    const trackingConfigured = !!(analytics.ga4_measurement_id || analytics.meta_pixel_id);
    const onStaffRoute = /^(\/admin|\/moderator)/.test(window.location.pathname);
    setVisible(consentRequired && !hasChoice && trackingConfigured && !onStaffRoute);
  }, [analytics]);

  const decide = (choice) => {
    setConsent(choice);
    setVisible(false);
    // Fire the custom event so AnalyticsProvider can boot immediately.
    window.dispatchEvent(new Event('indusfact:consent-change'));
  };

  if (!visible) return null;

  return (
    <div
      role='dialog'
      aria-live='polite'
      aria-label='Analytics consent'
      data-testid='consent-banner'
      className='fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[300] rounded-lg border border-border bg-background/95 backdrop-blur shadow-xl p-4'
    >
      <div className='flex items-start gap-3'>
        <div className='h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0'>
          <Cookie className='h-4 w-4 text-primary' />
        </div>
        <div className='flex-1 min-w-0'>
          <div className='font-semibold text-sm'>We use analytics to improve IndusFact</div>
          <p className='text-xs text-muted-foreground mt-1'>
            We use privacy-respecting analytics (Google Analytics &amp; Meta Pixel) to understand which listings and features are useful. No personal data is sold. You can change this anytime by clearing your browser storage.
          </p>
          <div className='mt-3 flex flex-wrap gap-2'>
            <Button
              size='sm'
              onClick={() => decide('granted')}
              data-testid='consent-accept'
            >
              Accept
            </Button>
            <Button
              size='sm'
              variant='outline'
              onClick={() => decide('denied')}
              data-testid='consent-reject'
            >
              Reject
            </Button>
          </div>
        </div>
        <button
          type='button'
          onClick={() => decide('denied')}
          className='text-muted-foreground hover:text-foreground shrink-0'
          aria-label='Dismiss (rejects analytics)'
          data-testid='consent-close'
        >
          <X className='h-4 w-4' />
        </button>
      </div>
    </div>
  );
};

export default ConsentBanner;
